import { Navigate, Outlet, ScrollRestoration, useLocation, useMatches } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { OnboardingState } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { onboardingRoute, useSessionState } from "@/shared/session/onboarding";
import { HouseholdProvider } from "@/shared/session/householdContext";
import { ErrorState, Icon, isChatShellHandle, LoadingState, Page, useChatKeyboardOpen } from "@/shared/ui";
import { SceneNavigationProvider } from "@/shared/ui/sceneNavigation";
import "./layout.css";

/**
 * 外壳不再挂底部主导航（用户 2026-10-03「下面不应该横在那里」「不管怎么样都不要像表单」「参考旅行青蛙」，
 * c84a HOME-SCENE-REFLOW v3 与多级页面单）：
 * - 主屏跟着 TA 走（用户 2026-09-27：「在家主屏是小屋、出门是地图」，见 ./primaryTab 的 usePrimaryTab、EntryRedirect）；
 * - 小屋（/home）：通讯器、回忆是场景左侧的小方块，地图在左上角；地图（/map）：左上角进小窝、去菜园，右侧有通讯器、回忆；
 * - 通讯器、回忆是第二层：左上角返回，回到 TA 此刻的主屏；“我的”在地图右上角头像。
 * 两种外壳都带 ps-shell--no-tabs（layout.css：底栏占高 --tabbar-h 按 0 算）：页面底部留白、贴底的输入框、菜园场景高度等凡是用它的地方一起收回，不留空洞。
 * 此刻没有任何页挂底栏；底栏的样式和这个变量等清理各处引用时一起去掉。
 * 模块不能新增底部 Tab。
 */

function ModeRibbon() {
  const { platform } = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  // 演示模式的数据说明横幅留着（I 10-09 批）：它说的是“这是演示世界、数据是测试的”，写给看演示的人，
  // 不是错误码、请求编号这类技术代码；只在 fixture 模式出现，live（正式玩家）下不渲染——tests/error-recovery-01.test.tsx 钉着。
  // 下面 live＋本地开发（env.isDev，只在 vite dev 下为真，构建出来的包里不会出现）那条是给开发看的连接状态，同样不进正式包。
  if (env.dataMode === "fixture") {
    return (
      <details className="ps-ribbon ps-ribbon--fixture">
        <summary>内部演示世界 · 数据说明</summary>
        <p>测试角色与数据，不代表真实账号、班次或商家；刷新后演示状态可能重置。</p>
      </details>
    );
  }
  if (!env.isDev) return null;
  if (meta.isError) {
    return (
      <div className="ps-ribbon ps-ribbon--error" role="status">
        <Icon name="alert" size={13} /> live · 未连上本地服务（{(meta.error as { code?: string }).code ?? "ERROR"}）
      </div>
    );
  }
  return (
    <div className="ps-ribbon" role="status">
      <Icon name="check" size={13} /> live · {meta.data ? `契约 ${meta.data.contract_version} · ${meta.data.backend_version} · 服务器 ${meta.data.server_time.slice(11, 19)}Z` : "连接本地服务…"}
    </div>
  );
}

/**
 * 账号里有没有已经住进来的宠物：任何一个已启用的家里有 join_step 为 moved_in 的（与切换栏列出的是同一批）。
 * 与 features/world_map/WorldGate 的 hasMovedInPet 是同一条规则（app 壳不引 features），tests/claude-6c2b-second-pet.test.tsx 两处一起钉。
 */
function hasMovedInPet(onboarding: OnboardingState): boolean {
  return (onboarding.households ?? []).some((household) => household.home_activated && household.pets.some((pet) => pet.join_step === "moved_in"));
}

/**
 * live 模式的入口守卫：带底部导航的页面只对“已登录且已入住”的账号开放；
 * 未登录去欢迎页，入住未完成回到对应的入住步骤。fixture 模式不设守卫（演示数据）。
 * 2026-09-24 巡检 P1：已经有宠物住进来的账号，第二只还在接待 / 入住时不再拦（原来整个账号的页面都被拉回接待页，后退也出不去）；
 * 只有一只都没住进来（真正的第一次入住）才带去入住步骤。
 */
function SessionGate() {
  const session = useSessionState();
  const location = useLocation();
  if (env.dataMode !== "live") return <HouseholdProvider userId={null}><Outlet /></HouseholdProvider>;
  if (session.isPending) {
    return (
      <Page>
        <LoadingState lines={2} label="正在确认登录状态…" />
      </Page>
    );
  }
  if (session.isError) {
    return (
      <Page>
        <ErrorState error={session.error} onRetry={() => void session.refetch()} />
      </Page>
    );
  }
  if (!session.data.authenticated) return <Navigate to="/welcome" replace state={{ from: location.pathname }} />;
  const onboarding = session.data.onboarding;
  if (onboarding && onboarding.step !== "active" && !hasMovedInPet(onboarding)) return <Navigate to={onboardingRoute(onboarding)} replace />;
  return <HouseholdProvider userId={session.data.user?.user_id ?? null}><Outlet /></HouseholdProvider>;
}

export function RootLayout() {
  // 聊天壳（S11 PC-00 做法 A）：路由 handle 标的聊天页（不按路径猜）。软键盘弹起时外壳带 data-keyboard="open"
  // （通讯器的输入区据此贴键盘上沿，见 communicator.css / useChatViewport）；没有底栏以后不再需要收起什么。
  const chatShell = useMatches().some((match) => isChatShellHandle(match.handle));
  const keyboardOpen = useChatKeyboardOpen();
  return (
    <div className={`ps-shell ps-shell--no-tabs${chatShell ? " ps-shell--chat" : ""}`} data-keyboard={chatShell && keyboardOpen ? "open" : undefined}>
      <SceneNavigationProvider>
        <ModeRibbon />
        <SessionGate />
        <ScrollRestoration />
      </SceneNavigationProvider>
    </div>
  );
}

export function BareLayout() {
  return (
    <div className="ps-shell ps-shell--bare ps-shell--no-tabs">
      <SceneNavigationProvider>
        <ModeRibbon />
        <Outlet />
        <ScrollRestoration />
      </SceneNavigationProvider>
    </div>
  );
}
